import {
  CheckCircleOutlineOutlined,
  Close,
  ErrorOutlineOutlined,
} from "@mui/icons-material";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";
import {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
} from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/form-field";
import { TabCompletionStatus } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/form-tab";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { useState } from "react";

export const QUESTIONNAIRE_BLOCK_COMPLETION_HINT_FIELD_TYPE =
  "questionnaireBlockCompletionHint" as const;

type HintVariant =
  | "submitBlocked"
  | "notComplete"
  | "blockComplete"
  | "allComplete"
  | "submitted";

const t = (language: string, key: string, fallback: string): string => {
  const msg = getLocalizedMessage(language, key);
  return !msg || msg === key ? fallback : msg;
};

const HINT: Record<
  HintVariant,
  { title: (l: string) => string; description: (l: string) => string }
> = {
  submitBlocked: {
    title: (l) =>
      t(
        l,
        "completionHint.submitBlocked.title",
        "Please review the assessment and resolve the problems"
      ),
    description: (l) =>
      t(
        l,
        "completionHint.submitBlocked.description",
        "The assessment cannot be submitted yet. Blocks with open problems are marked in the navigation on the left. You can also save and close, and come back later."
      ),
  },
  notComplete: {
    title: (l) =>
      t(l, "completionHint.notComplete.title", "Block not yet complete"),
    description: (l) =>
      t(
        l,
        "completionHint.notComplete.description",
        "This block is not yet complete. You can continue now or return later to complete it. All required fields must be completed before the review can be submitted."
      ),
  },
  blockComplete: {
    title: (l) => t(l, "completionHint.blockComplete.title", "Block completed"),
    description: (l) =>
      t(
        l,
        "completionHint.blockComplete.description",
        "This block is complete. Continue with the next block to finish the review."
      ),
  },
  allComplete: {
    title: (l) =>
      t(l, "completionHint.allComplete.title", "Review ready for submission"),
    description: (l) =>
      t(
        l,
        "completionHint.allComplete.description",
        "You have completed all required blocks. Review your responses and submit the review to finalize the results."
      ),
  },
  submitted: {
    title: (l) => t(l, "completionHint.submitted.title", "Review submitted"),
    description: (l) =>
      t(
        l,
        "completionHint.submitted.description",
        "This review has already been submitted and finalized. The responses are locked."
      ),
  },
};

export type QuestionnaireBlockCompletionHintFieldDefinition =
  BaseFormFieldDefinition<
    typeof QUESTIONNAIRE_BLOCK_COMPLETION_HINT_FIELD_TYPE
  > & {
    /** 1-based position of this block in the review, used by the "Block N completed" hint. */
    blockNumber: number;
    /** Reads the block's live completion status; called on every render so the hint stays in sync. */
    getCompletionStatus: () => TabCompletionStatus | undefined;
    /** Whether every block in the review is complete, for the "ready for submission" hint. */
    getAllBlocksComplete: () => boolean;
    /** Whether the review has already been submitted (degreeOfCompletion = Done). */
    isAlreadySubmitted?: boolean;
    /** Whether a Submit attempt was refused because validation errors remain. */
    getSubmitBlocked?: () => boolean;
    language?: string;
  };

export const QuestionnaireBlockCompletionHintField = ({
  blockNumber,
  getCompletionStatus,
  getAllBlocksComplete,
  isAlreadySubmitted,
  getSubmitBlocked,
  language = "en",
}: QuestionnaireBlockCompletionHintFieldDefinition &
  AdditionalFormFieldProps) => {
  const theme = useTheme();
  const status = getCompletionStatus();
  const allBlocksComplete = getAllBlocksComplete();
  const submitBlocked = getSubmitBlocked?.() ?? false;
  const dismissKey = status
    ? `${status}-${allBlocksComplete}-${isAlreadySubmitted}-${submitBlocked}`
    : null;
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  if (!status || dismissedKey === dismissKey) return null;

  const isComplete = status === "complete";
  // Same three block colours as the sidebar: grey until complete, green once
  // complete, purple for the whole review once it is submitted.
  const color = submitBlocked
    ? theme.palette.error.main
    : isAlreadySubmitted
      ? theme.palette.info.main
      : isComplete
        ? theme.palette.success.main
        : grey[500];

  // A refused Submit outranks the per-block hints: it is the reason the user is
  // being sent back through the assessment. Already-submitted outranks
  // "not complete" too: a blueprint can grow a new mandatory question after
  // submission, and a locked, submitted review must not tell the user to fill
  // in a field they can no longer edit.
  const variantConditions: [boolean, HintVariant][] = [
    [submitBlocked, "submitBlocked"],
    [isAlreadySubmitted ?? false, "submitted"],
    [!isComplete, "notComplete"],
    [allBlocksComplete, "allComplete"],
  ];
  const variant: HintVariant =
    variantConditions.find(([cond]) => cond)?.[1] ?? "blockComplete";

  const Icon = submitBlocked
    ? ErrorOutlineOutlined
    : CheckCircleOutlineOutlined;
  const hint = HINT[variant];
  const title =
    variant === "blockComplete"
      ? `${t(language, "completionHint.blockComplete.title", "Block")} ${blockNumber} ${t(language, "completionHint.blockComplete.completed", "completed")}`
      : hint.title(language);
  const description = hint.description(language);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        borderRadius: "8px",
        padding: "12px 16px",
        backgroundColor: alpha(color, 0.08),
      }}
    >
      <Icon sx={{ color, mt: "2px", fontSize: 20 }} />
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{title}</Typography>
        <Typography sx={{ fontSize: 13, color: grey[700], mt: 0.5 }}>
          {description}
        </Typography>
      </Box>
      <IconButton
        size="small"
        onClick={() => dismissKey && setDismissedKey(dismissKey)}
        sx={{ mt: "-4px" }}
      >
        <Close fontSize="small" />
      </IconButton>
    </Box>
  );
};
